import React, { useState } from 'react';
import DrawerFilter from '../DrawerFilter';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Settings } from 'lucide-react';
import '../../styles/components/_header.scss';

interface PageInfo {
  title: string;
  subtitle: string;
}

interface HeaderProps {
  title: string;
  onConfigureClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ title, onConfigureClick }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  // Função para obter informações do botão de criação baseado na rota atual
  const getCreateButtonInfo = () => {
    const currentPath = location.pathname;

    switch (true) {
      case currentPath.startsWith('/propostas') && !currentPath.includes('/novo') && !currentPath.includes('/editar') && !currentPath.match(/\/propostas\/\d+/):
        return {
          text: 'Nova proposta',
          path: '/propostas/novo',
          show: true
        };

      case currentPath.startsWith('/empreendimentos') && !currentPath.includes('/novo') && !currentPath.match(/\/empreendimentos\/\d+/):
        return {
          text: 'Novo empreendimento',
          path: '/empreendimentos/novo',
          show: true
        };

      case (currentPath.startsWith('/incorporadoras') || currentPath.startsWith('/Incorporadoras')) && !currentPath.includes('/novo'):
        return {
          text: 'Nova incorporadora',
          path: '/incorporadoras/novo',
          show: true
        };

      case currentPath.startsWith('/usuarios') && !currentPath.includes('/novo'):
        return {
          text: 'Novo usuário',
          path: '/usuarios/novo',
          show: true
        };

      case currentPath === '/outros/FAQ':
        return {
          text: 'Nova pergunta',
          path: '/outros/FAQ', // Você pode ajustar a rota conforme necessário
          show: true
        };

      case currentPath.includes('/ViabilitySpreadsheet'):
        return {
          text: 'Nova planilha',
          path: '/outros/ViabilitySpreadsheet', // Você pode ajustar a rota conforme necessário
          show: true
        };

      default:
        return {
          text: '',
          path: '',
          show: false
        };
    }
  };

  const handleCreateClick = () => {
    const buttonInfo = getCreateButtonInfo();
    if (buttonInfo.show && buttonInfo.path) {
      navigate(buttonInfo.path);
    }
  };

  const handleConfigureClick = () => {
    if (onConfigureClick) {
      onConfigureClick();
    } else {
      // Dispara evento customizado para abrir o modal
      window.dispatchEvent(new Event('openConfigModal'));
    }
  };

  const showConfigureButton = () => {
    return location.pathname === '/leads-site';
  };

  // Mapeamento de títulos e subtítulos para cada página
  const getPageInfo = (): PageInfo => {
    const currentPath = location.pathname;

    switch (true) {
      case currentPath === '/home':
        return {
          title: 'Dashboard',
          subtitle: 'Visualize suas informações gerais e acompanhe o desempenho do sistema.'
        };

      case currentPath.startsWith('/propostas'):
        if (currentPath.includes('/novo')) {
          return {
            title: 'Nova Proposta',
            subtitle: 'Preencha os dados necessários para criar uma nova proposta.'
          };
        } else if (currentPath.includes('/editar')) {
          return {
            title: 'Editar Proposta',
            subtitle: 'Modifique as informações da proposta selecionada.'
          };
        } else if (currentPath.match(/\/propostas\/\d+/)) {
          return {
            title: 'Detalhes da Proposta',
            subtitle: 'Visualize todas as informações e histórico da proposta.'
          };
        } else {
          return {
            title: 'Propostas',
            subtitle: 'Gerencie todas as propostas do sistema e acompanhe seus status.'
          };
        }

      case currentPath.startsWith('/empreendimentos'):
        if (currentPath.includes('/novo')) {
          return {
            title: 'Novo Empreendimento',
            subtitle: 'Cadastre um novo empreendimento no sistema.'
          };
        } else if (currentPath.match(/\/empreendimentos\/\d+/)) {
          return {
            title: 'Detalhes do Empreendimento',
            subtitle: 'Visualize todas as informações do empreendimento selecionado.'
          };
        } else {
          return {
            title: 'Empreendimentos',
            subtitle: 'Gerencie todos os empreendimentos cadastrados e acompanhe as informações.'
          };
        }

      case currentPath.startsWith('/incorporadoras') || currentPath.startsWith('/Incorporadoras'):
        if (currentPath.includes('/novo')) {
          return {
            title: 'Nova Incorporadora',
            subtitle: 'Cadastre uma nova incorporadora no sistema.'
          };
        } else {
          return {
            title: 'Incorporadoras',
            subtitle: 'Gerencie todas as incorporadoras cadastradas e suas informações.'
          };
        }

      case currentPath.startsWith('/usuarios'):
        if (currentPath.includes('/novo')) {
          return {
            title: 'Novo Usuário',
            subtitle: 'Cadastre um novo usuário no sistema.'
          };
        } else {
          return {
            title: 'Usuários',
            subtitle: 'Gerencie todos os usuários do sistema e controle suas permissões.'
          };
        }

      case currentPath === '/leads':
        return {
          title: 'Leads',
          subtitle: 'Visualize e gerencie todos os leads cadastrados e seus status.'
        };

      case currentPath === '/leads-site':
        return {
          title: 'Leads site',
          subtitle: 'Gerencie os leads originados do site e acompanhe conversões.'
        };

      case currentPath === '/outros/FAQ':
        return {
          title: 'Tópicos frequentes (FAQ)',
          subtitle: 'Gerencie as perguntas e respostas mais comuns do sistema.'
        };

      case currentPath.includes('/scheduling') || currentPath.includes('/Scheduling'):
        return {
          title: 'Agenda de visitas',
          subtitle: 'Gerencie todas as visitas agendadas e acompanhe o cronograma.'
        };

      case currentPath.includes('/ViabilitySpreadsheet'):
        return {
          title: 'Planilha de viabilidade',
          subtitle: 'Analise a viabilidade dos projetos através de dados financeiros.'
        };

      case currentPath.includes('/DocsIncorporadores'):
        return {
          title: 'Documentos de incorporadoras',
          subtitle: 'Gerencie todos os documentos das incorporadoras cadastradas.'
        };

      case currentPath.includes('/DocsEnterprises'):
        return {
          title: 'Documentos de empreendimentos',
          subtitle: 'Organize e gerencie os documentos dos empreendimentos.'
        };

      case currentPath.includes('/ConstructionSteps'):
        return {
          title: 'Etapas da construção',
          subtitle: 'Gerencie as etapas e acompanhe o progresso das obras.'
        };

      case currentPath.startsWith('/perfil'):
        return {
          title: 'Editar perfil',
          subtitle: 'Atualize suas informações pessoais e configurações da conta.'
        };

      default:
        return {
          title: title || 'HomeCorp',
          subtitle: 'Sistema de gestão HomeCorp'
        };
    }
  };

  const pageInfo = getPageInfo();
  const createButtonInfo = getCreateButtonInfo();

  return (
    <>
      <header className="header-main">
        <div className="header-content-main">
          <h1 className="page-title">{pageInfo.title}</h1>
          <p className="page-subtitle">{pageInfo.subtitle}</p>
        </div>

        <div className="header-buttons">
          {showConfigureButton() && (
            <button className="configure-button" onClick={handleConfigureClick}>
              <Settings size={16} />
              Configurar premissas
            </button>
          )}

          {createButtonInfo.show && (
            <button className="create-button" onClick={handleCreateClick}>
              <Plus size={16} />
              {createButtonInfo.text}
            </button>
          )}
        </div>

        {
          createButtonInfo.show && (
            <button className="create-button" onClick={handleCreateClick}>
              <Plus size={16} />
              {createButtonInfo.text}
            </button>
          )
        }
      </header >

      <DrawerFilter isOpen={isDrawerOpen} onClose={toggleDrawer} />
    </>
  );
};


export default Header;